<i18n
  src="@/locales/views/agro_exporter/production/basic_container_export/new_exportation.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/exportation.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/container.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go-back-link')"
    :name="routeName('show_process_exportations')"
    :params="{ agro_exporter_process_id: agro_exporter_process_id }"
  />

  <bread-crumbs :labels="[module_name, agro_exporter_process?.name]" />

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-gray-200">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('booking_code')]">
                <template v-if="exportation_type_id == 0">
                  {{ $t("model.exportation.booking_code_0") }}
                </template>
                <template v-else-if="exportation_type_id == 1">
                  {{ $t("model.exportation.booking_code_1") }}
                </template>
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="booking_code"
                  :class="['input-text', isInvalid('booking_code')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label
                :class="['label-form', isInvalidLabel('exportation_type_id')]"
              >
                {{ $t("model.exportation.exportation_type") }}
              </label>
              <div class="mt-1">
                <exportation-type-selector v-model="exportation_type_id" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('dam_code')]">
                {{ $t("model.exportation.dam_code") }}
              </label>
              <div class="mt-1">
                <input
                  type="text"
                  v-model="dam_code"
                  :class="['input-text', isInvalid('dam_code')]"
                />
              </div>
            </div>
            <div class="sm:col-span-3" v-if="agro_exporter_process.intermediate">
              <label :class="['label-form', isInvalidLabel('intermediated_agro_exporter')]">
                {{ $t('model.exportation.intermediated_agro_exporter_id') }}
              </label>
              <div class="mt-1">
                <select-form
                  v-model="intermediated_agro_exporter_id"
                  :options="agro_exporters"
                />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('status_id')]">
                {{ $t("model.exportation.status") }}
              </label>
              <div class="mt-1">
                <exportation-status-selector v-model="status_id" />
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.exportation.containers") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="containers_available_filtered.length > 0"
                  :options="containers_available_filtered"
                  v-model="containers"
                  label="container_code"
                  :multiple="true"
                />
                <alert-warning
                  v-if="containers_available_filtered == 0"
                  :warning_data="{
                    title: $t('labels.not_available_containers')
                  }"
                />
              </div>
            </div>

            <div class="sm:col-span-6" v-if="containers.length > 0">
              <div class="table-first-container">
                <div class="table-second-container">
                  <div class="table-third-container">
                    <table class="table">
                      <thead class="table-header">
                        <tr>
                          <th scope="col" class="table-header-cell">
                            {{ $t("model.container.container_code") }}
                          </th>
                          <th scope="col" class="table-header-cell">
                            {{ $t("model.container.packing_house") }}
                          </th>
                          <th scope="col" class="table-header-cell">
                            {{ $t("model.container.producers") }}
                          </th>
                          <th scope="col" class="table-header-cell">
                            {{ $t("model.container.customer") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="table-body">
                        <tr
                          class="table-row"
                          v-for="s_container in selectedContainers"
                          :key="s_container.id"
                        >
                          <td class="table-cell">
                            {{ s_container.container_code }}
                          </td>
                          <td class="table-cell">
                            {{ s_container.packing_house_names.join(", ") }}
                          </td>
                          <td class="table-cell"></td>
                          <td class="table-cell">
                            {{ s_container.customer?.name }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('origin_export')]">
                {{ $t("model.exportation.origin_export") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="export_ports_origin.length > 0"
                  :options="export_ports_origin"
                  v-model="origin_export_id"
                  :is_invalid="isInvalidSelect('origin_export')"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label
                :class="['label-form', isInvalidLabel('destination_export')]"
              >
                {{ $t("model.exportation.destination_export") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="export_ports_destination.length > 0"
                  :options="export_ports_destination"
                  v-model="destination_export_id"
                  :is_invalid="isInvalidSelect('destination_export')"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.exportation.departured_at") }}
              </label>
              <div class="mt-1">
                <date-form v-model="departured_at" />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.exportation.arrived_at") }}
              </label>
              <div class="mt-1">
                <date-form v-model="arrived_at" />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label :class="['label-form', isInvalidLabel('shipping_line')]">
                {{ $t("model.exportation.shipping_line") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="shipping_lines.length > 0"
                  :options="shipping_lines"
                  v-model="shipping_line_id"
                  :is_invalid="isInvalidSelect('shipping_line')"
                />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.exportation.ship") }}
              </label>
              <div class="mt-1">
                <input type="text" v-model="ship" class="input-text" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowProcess()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="createExportation()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import BasicContainerExportQRequester from "@/views/agro_exporter/requesters/production/basic_container_export/query.js";
import BasicContainerExportMERequester from "@/views/agro_exporter/requesters/production/basic_container_export/manage_exportation.js";
import moment from "moment";
import ExportationStatusSelector from "@/components/views/agro_exporter/production/basic_container_export/ExportationStatusSelector.vue";
import ExportationTypeSelector from "@/components/views/agro_exporter/production/basic_container_export/ExportationTypeSelector.vue";

export default {
  name: "AEProductionBasicContainerExportNewContainer",
  components: {
    ExportationStatusSelector,
    ExportationTypeSelector
  },
  props: ["agro_exporter_process_id", "agro_exporter_id"],
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      agro_exporter_process: {},
      booking_code: null,
      dam_code: null,
      departured_at: null,
      arrived_at: null,
      origin_export_id: null,
      export_ports_origin: [],
      destination_export_id: null,
      export_ports_destination: [],
      shipping_line_id: null,
      shipping_lines: [],
      ship: null,
      containers: [],
      containers_available: [],
      status_id: null,
      exportation_statuses: [],
      exportation_type_id: 0,
      agro_exporters: [],
      intermediated_agro_exporter_id: null,
    };
  },
  methods: {
    fetchData: async function () {
      await this.fetchAgroExporterProcess();
      await this.fetchAvailableToExportContainers();
      await this.fetchExportPortsOrigin();
      await this.fetchExportPortsDestination();
      await this.fetchIntermediatedAgroExporters();
      await this.fetchShippingLines();
      this.load_general_data = true;
    },
    fetchAgroExporterProcess: async function () {
      let response =
        await BasicContainerExportQRequester.fetchAgroExporterProcess(
          this.agro_exporter_process_id
        );
      this.agro_exporter_process = response.data;
    },
    fetchAvailableToExportContainers: async function () {
      let response =
        await BasicContainerExportMERequester.fetchAvailableToExportContainers(
          this.agro_exporter_process_id
        );
      this.containers_available = response.data;
      this.containers_available.forEach(c => {
        if (!c.container_code) c.container_code = c.container_request_code;
      });
    },
    fetchExportPortsOrigin: async function () {
      let response =
        await BasicContainerExportQRequester.fetchExportPortsOrigin(
          this.agro_exporter_process_id
        );
      this.export_ports_origin = response.data;
    },
    fetchExportPortsDestination: async function () {
      let response =
        await BasicContainerExportQRequester.fetchExportPortsDestination(
          this.agro_exporter_process_id
        );
      this.export_ports_destination = response.data;
    },
    fetchIntermediatedAgroExporters: async function () {
      let response = await BasicContainerExportQRequester.fetchIntermediatedAgroExporters(
        this.agro_exporter_process_id
      );
      this.agro_exporters = response.data;
    },
    fetchShippingLines: async function () {
      let response = await BasicContainerExportQRequester.fetchShippingLines(
        this.agro_exporter_process_id
      );
      this.shipping_lines = response.data;
    },
    fetchExportationStatuses: async function () {
      let response =
        await BasicContainerExportQRequester.fetchExportationStatuses(
          this.agro_exporter_process_id
        );
      this.exportation_statuses = response.data;
    },
    createExportation: async function () {
      this.proc_save_button = true;
      try {
        let response = await BasicContainerExportMERequester.createExportation(
          this.agro_exporter_process_id,
          {
            booking_code: this.booking_code,
            departured_at: this.departured_at_formatted,
            arrived_at: this.arrived_at_formatted,
            origin_export_id: this.origin_export_id,
            destination_export_id: this.destination_export_id,
            shipping_line_id: this.shipping_line_id,
            ship: this.ship,
            status_id: this.status_id,
            exportation_type_id: this.exportation_type_id,
            dam_code: this.dam_code,
            intermediated_agro_exporter_id: this.intermediated_agro_exporter_id
          },
          this.containers
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProcess();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_process_exportations"),
        params: {
          agro_exporter_process_id: this.agro_exporter_process_id
        }
      });
    }
  },
  computed: {
    departured_at_formatted: function () {
      return moment(this.departured_at).format("YYYY-MM-DD");
    },
    arrived_at_formatted: function () {
      return moment(this.arrived_at).format("YYYY-MM-DD");
    },
    selectedContainers: function () {
      let selected_containers = [];
      for (let i = 0; i < this.containers_available.length; i++) {
        if (this.containers.includes(this.containers_available[i].id)) {
          selected_containers.push(this.containers_available[i]);
        }
      }
      return selected_containers;
    },
    containers_available_filtered: function () {
      if (this.intermediated_agro_exporter_id) {
        return this.containers_available.filter(
          (container) =>
            container.agro_exporter_id === this.intermediated_agro_exporter_id
        );
      }
      return this.containers_available;
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => {
        this.fetchData();
      })
    );
  }
};
</script>
